import {
  Archive,
  ArchiveRestore,
  CircleX,
  FileQuestion,
  FolderOpen,
} from 'lucide-preact'
import { computed, effect } from '@preact/signals'
import { api } from '../lib/api.ts'
import { A, navigate, url } from '@01edu/signal-router'
import { isAdmin, onEvent, user } from '../lib/session.ts'
import {
  ProjectSyncIndicator,
  ProjectSyncModal,
  syncProjectsById,
} from '../ui/sync-status.tsx'
import { DialogModal } from '../ui/dialog.tsx'
import { notify } from '../ui/notifications.tsx'
import { ProjectDomain } from '../ui/project-domain.tsx'

const projects = api['GET/api/projects'].signal()
const archiveSignal = api['POST/api/project/archive'].signal()
const unarchiveSignal = api['POST/api/project/unarchive'].signal()

const showArchived = computed(() => url.params.view === 'archived')
const currentPage = computed(() => Number(url.params.page) || 1)
const projectList = computed(() => projects.data?.projects || [])
const projectCount = computed(() => projects.data?.projectCount || 0)
const LIMIT = 10

const refreshProjectList = () => {
  if (!user.data) return
  const offset = currentPage.value - 1
  projects.fetch({
    limit: String(LIMIT),
    offset: String(offset * LIMIT),
    view: showArchived.value ? 'archived' : undefined,
  })
}

effect(refreshProjectList)
onEvent('PROJECT_CREATED', refreshProjectList)
onEvent('PROJECT_ARCHIVED', () => {
  navigate({
    params: { dialog: null, 'archive-project': null },
    replace: true,
  })
  refreshProjectList()
  notify({ status: 'success', description: 'Project archived' })
})
onEvent('PROJECT_UNARCHIVED', () => {
  navigate({
    params: { dialog: null, 'unarchive-project': null },
    replace: true,
  })
  refreshProjectList()
  notify({ status: 'success', description: 'Project unarchived' })
})

const PageLink = ({ page }: { page: number }) => (
  <A
    params={{ page }}
    class={[
      'join-item btn',
      currentPage.value === page ? 'btn-primary' : 'btn-ghost',
    ]
      .join(' ')}
  >
    {page}
  </A>
)

const generatePageList = (amount: number, page: number) => {
  if (amount < 2) return []
  if (amount < 8) return [...Array(amount + 1).keys().drop(1)]
  if (page < 4) return [1, 2, 3, 4, 0, amount - 1, amount]
  if (page > amount - 3) {
    return [1, 2, 0, amount - 3, amount - 2, amount - 1, amount]
  }
  return [
    1,
    page === 4 ? 2 : 0,
    page - 1,
    page,
    page + 1,
    page === amount - 3 ? amount - 1 : 0,
    amount,
  ]
}

const Pagination = ({ amount }: { amount: number }) => {
  return (
    <div class='join flex justify-end mt-4'>
      {generatePageList(amount, currentPage.value).map((page, i) =>
        page
          ? <PageLink key={page} page={page} />
          : <span key={-i} class='btn join-item'>...</span>
      )}
    </div>
  )
}

const handleArchive = () => {
  const projectId = Number(url.params['archive-project'])
  if (projectId) archiveSignal.fetch({ projectId })
}

const handleUnarchive = () => {
  const projectId = Number(url.params['unarchive-project'])
  if (projectId) unarchiveSignal.fetch({ projectId })
}

const ArchiveProjectModal = () => (
  <DialogModal id='archive-project'>
    <h3 class='text-lg font-bold'>Archive Project</h3>
    <div class='py-4'>
      <p class='mb-4'>
        Are you sure you want to archive this project? It will no longer sync
        and will be moved to the archived tab.
      </p>

      <div class='flex gap-3 justify-end'>
        <A
          class='btn btn-ghost'
          params={{ dialog: null, 'archive-project': null }}
          replace
        >
          Cancel
        </A>
        <button
          type='button'
          class='btn btn-warning'
          disabled={!!archiveSignal.pending}
          onClick={handleArchive}
        >
          {archiveSignal.pending
            ? (
              <>
                <span class='loading loading-spinner loading-sm' />
                Archiving...
              </>
            )
            : (
              <>
                <Archive class='h-4 w-4' />
                Archive
              </>
            )}
        </button>
      </div>

      {archiveSignal.error && (
        <div class='alert alert-error mt-4'>
          <span>{archiveSignal.error.message}</span>
        </div>
      )}
    </div>
  </DialogModal>
)

const UnarchiveProjectModal = () => (
  <DialogModal id='unarchive-project'>
    <h3 class='text-lg font-bold'>Unarchive Project</h3>
    <div class='py-4'>
      <p class='mb-4'>
        This project will be restored and syncing will resume.
      </p>

      <div class='flex gap-3 justify-end'>
        <A
          class='btn btn-ghost'
          params={{ dialog: null, 'unarchive-project': null }}
          replace
        >
          Cancel
        </A>
        <button
          type='button'
          class='btn btn-primary'
          disabled={!!unarchiveSignal.pending}
          onClick={handleUnarchive}
        >
          {unarchiveSignal.pending
            ? (
              <>
                <span class='loading loading-spinner loading-sm' />
                Unarchiving...
              </>
            )
            : (
              <>
                <ArchiveRestore class='h-4 w-4' />
                Unarchive
              </>
            )}
        </button>
      </div>

      {unarchiveSignal.error && (
        <div class='alert alert-error mt-4'>
          <span>{unarchiveSignal.error.message}</span>
        </div>
      )}
    </div>
  </DialogModal>
)

const ViewTabs = () => (
  <div
    role='tablist'
    class='tabs tabs-box bg-base-100 border border-base-300 w-fit mb-6 p-1'
  >
    <A
      role='tab'
      params={{ view: null, page: null }}
      class={`tab gap-2 ${!showArchived.value ? 'tab-active' : ''}`}
    >
      <FolderOpen class='h-4 w-4' />
      Active
    </A>
    <A
      role='tab'
      params={{ view: 'archived', page: null }}
      class={`tab gap-2 ${showArchived.value ? 'tab-active' : ''}`}
    >
      <Archive class='h-4 w-4' />
      Archived
    </A>
  </div>
)

const ProjectsTable = () => (
  <div class='bg-base-100 border border-base-300 rounded-box shadow-sm overflow-hidden'>
    <div class='overflow-x-auto'>
      <table class='w-full text-left border-collapse'>
        <thead>
          <tr class='bg-base-100 border-b border-base-300'>
            <th class='px-6 py-4 text-xs font-semibold text-base-content/60 uppercase'>
              Project
            </th>
            {!showArchived.value && (
              <th class='px-6 py-4 text-xs font-semibold text-base-content/60 uppercase whitespace-nowrap'>
                Sync Status
              </th>
            )}
            <th class='px-6 py-4 text-xs font-semibold text-base-content/60 uppercase w-1/5 text-right'>
              Actions
            </th>
          </tr>
        </thead>
        <tbody class='divide-y divide-base-200'>
          {projectList.value.map(({ projectDomain, projectId }) => (
            <tr key={projectId} class='hover:bg-base-300/20'>
              <td class='px-6 py-5 align-middle'>
                <div class='flex flex-col'>
                  <span class='text-sm font-medium text-base-content'>
                    <ProjectDomain domain={projectDomain} />
                  </span>
                  <span class='font-mono text-xs text-base-content/50'>
                    #{projectId}
                  </span>
                </div>
              </td>
              {!showArchived.value && (
                <td class='px-6 py-5 align-middle'>
                  <ProjectSyncIndicator
                    project={syncProjectsById.value.get(projectId)}
                  />
                </td>
              )}
              <td class='px-6 py-5 align-middle text-right'>
                <div class='flex items-center gap-2 justify-end'>
                  {!showArchived.value && (
                    <A
                      class='btn btn-sm btn-primary'
                      params={{ project: projectId }}
                    >
                      Open
                    </A>
                  )}
                  {!isAdmin.value ? null : showArchived.value
                    ? (
                      <A
                        class='btn btn-sm btn-outline btn-primary gap-1'
                        params={{
                          dialog: 'unarchive-project',
                          'unarchive-project': projectId,
                        }}
                        replace
                      >
                        <ArchiveRestore class='h-3.5 w-3.5' />
                        Unarchive
                      </A>
                    )
                    : (
                      <A
                        class='btn btn-sm btn-outline btn-warning gap-1'
                        params={{
                          dialog: 'archive-project',
                          'archive-project': projectId,
                        }}
                        replace
                      >
                        <Archive class='h-3.5 w-3.5' />
                        Archive
                      </A>
                    )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)
const NoProjectMessage = () => (
  <div class='text-center mt-12'>
    <FileQuestion class='h-16 w-16 mx-auto mb-4 text-primary opacity-80' />
    <p class='text-2xl font-bold mb-4'>
      {showArchived.value ? 'No archived projects' : 'No projects found'}
    </p>
    <p class='text-lg mb-6 text-base-content/70'>
      {showArchived.value
        ? 'There are no archived projects.'
        : "You haven't created any projects yet."}
    </p>
  </div>
)

export const ErrorDisplay = ({ error }: { error: Error }) => (
  <div class='flex flex-col items-center justify-center py-12'>
    <CircleX class='text-error mb-4' />
    <p class='text-xl text-error font-bold'>Error loading projects</p>
    <p class='mt-2'>{error.message}</p>
    <A class='btn btn-primary mt-6' params={{ page: currentPage.value }}>
      Try Again
    </A>
  </div>
)

const AllProjects = () => {
  const totalPages = Math.ceil(projectCount.value / LIMIT)

  return (
    <div class='max-w-300 mx-auto pt-4 pb-6 px-4'>
      {isAdmin.value && <ViewTabs />}
      {projectCount.value > 0
        ? (
          <>
            <ProjectsTable />
            {totalPages > 1 && <Pagination amount={totalPages} />}
          </>
        )
        : <NoProjectMessage />}
    </div>
  )
}

const ProjectList = () => {
  if (projects.error) return <ErrorDisplay error={projects.error} />
  if (!projects.data) return null
  return <AllProjects />
}

// The sync modal is for everyone: owners see the sync indicator too, and it
// links to this dialog.
export const Projects = () => (
  <>
    <ProjectList />
    <ProjectSyncModal />
    {isAdmin.value && (
      <>
        <ArchiveProjectModal />
        <UnarchiveProjectModal />
      </>
    )}
  </>
)
